import { useEventos, useTiposAnomalia, useUpdateEvento, useDeleteEvento } from '../hooks/useEventos'
import { useEventosConteo } from '../hooks/useEventosConteo'
import { useEventosConteoPorCentro } from '../hooks/useEventosConteoPorCentro'
import { useCentrosTrabajo } from '../hooks/useCentrosTrabajo'
import { useThumbnails } from '../hooks/useThumbnails'
import { useFiltersStore } from '../store/filtersStore'
import { ESTADOS, ESTADOS_LABELS, SEVERIDADES_LABELS, SEVERIDADES_DOT_COLORS } from '../utils/constants'
import { formatRelativeTime } from '../utils/formatters'
import { addBase64Prefix } from '../utils/imageHelpers'
import { useNavigate } from 'react-router-dom'
import Loading from '../components/common/Loading'
import EmptyState from '../components/common/EmptyState'
import CentrosSelector from '../components/filters/CentrosSelector'
import TipoAnomaliaIcon from '../components/common/TipoAnomaliaIcon'
import { Listbox, Transition, Portal, Dialog } from '@headlessui/react'
import { Fragment, useState, useRef, useMemo } from 'react'
import {
  BellAlertIcon,
  EyeIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  CheckIcon,
  TrashIcon,
  Squares2X2Icon,
  ListBulletIcon,
} from '@heroicons/react/24/outline'

// Mapeo de iconos para cada estado
const ESTADOS_ICONS = {
  [ESTADOS.NUEVO]: BellAlertIcon,
  [ESTADOS.VISTO]: EyeIcon,
  [ESTADOS.EN_PROCESO]: WrenchScrewdriverIcon,
  [ESTADOS.RESUELTO]: CheckCircleIcon,
  [ESTADOS.DESCARTADO]: XCircleIcon,
}

// Componente selector de estado compacto para la lista
const EstadoSelectorInline = ({ idEvento, estadoActual, onUpdate }) => {
  const updateEventoMutation = useUpdateEvento()
  const buttonRef = useRef(null)

  const estadosConfig = {
    [ESTADOS.NUEVO]: { label: 'Nuevo', icon: BellAlertIcon, color: 'text-blue-600' },
    [ESTADOS.VISTO]: { label: 'Visto', icon: EyeIcon, color: 'text-purple-600' },
    [ESTADOS.EN_PROCESO]: { label: 'En Proceso', icon: WrenchScrewdriverIcon, color: 'text-orange-600' },
    [ESTADOS.RESUELTO]: { label: 'Resuelto', icon: CheckCircleIcon, color: 'text-green-600' },
    [ESTADOS.DESCARTADO]: { label: 'Descartado', icon: XCircleIcon, color: 'text-gray-600' },
  }

  const handleChange = (nuevoEstado) => {
    updateEventoMutation.mutate({
      id: idEvento,
      data: { idEstadoEvento: parseInt(nuevoEstado) },
    })
    if (onUpdate) onUpdate()
  }

  const getBadgeClass = (estado) => {
    const classes = {
      1: 'badge-nuevo',
      2: 'badge-visto',
      3: 'badge-proceso',
      4: 'badge-resuelto',
      5: 'badge-descartado',
    }
    return `badge ${classes[estado] || ''}`
  }

  return (
    <Listbox value={estadoActual} onChange={handleChange} disabled={updateEventoMutation.isPending}>
      {({ open }) => (
        <>
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <Listbox.Button
              ref={buttonRef}
              onClick={(e) => e.stopPropagation()}
              className={`${getBadgeClass(estadoActual)} cursor-pointer hover:opacity-80 transition-opacity inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold`}
            >
              <span>{ESTADOS_LABELS[estadoActual]}</span>
              <ChevronDownIcon className="w-4 h-4" />
            </Listbox.Button>
          </div>
          {open && (
            <Portal>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options
                  static
                  className="fixed w-48 rounded-lg bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                  style={{
                    top: buttonRef.current ? buttonRef.current.getBoundingClientRect().bottom + 8 : 0,
                    left: buttonRef.current ? buttonRef.current.getBoundingClientRect().left : 0,
                  }}
                >
                  <div className="p-2" onClick={(e) => e.stopPropagation()}>
                    {Object.entries(estadosConfig).map(([key, config]) => {
                      const Icon = config.icon
                      return (
                        <Listbox.Option
                          key={key}
                          value={parseInt(key)}
                          className={({ active }) =>
                            `flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                              active ? 'bg-gray-100' : ''
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <Icon className={`w-4 h-4 ${config.color}`} />
                              <span className={`flex-1 text-sm text-gray-900 ${selected ? 'font-semibold' : 'font-normal'}`}>
                                {config.label}
                              </span>
                              {selected && <CheckIcon className="w-4 h-4 text-primary" />}
                            </>
                          )}
                        </Listbox.Option>
                      )
                    })}
                  </div>
                </Listbox.Options>
              </Transition>
            </Portal>
          )}
        </>
      )}
    </Listbox>
  )
}

// Componente selector de severidad compacto para la lista
const SeveridadSelectorInline = ({ idEvento, severidadActual, onUpdate }) => {
  const updateEventoMutation = useUpdateEvento()
  const buttonRef = useRef(null)

  const severidadConfig = {
    1: { label: 'Baja', color: SEVERIDADES_DOT_COLORS[1] },
    2: { label: 'Media', color: SEVERIDADES_DOT_COLORS[2] },
    3: { label: 'Alta', color: SEVERIDADES_DOT_COLORS[3] },
  }

  const handleChange = (nuevaSeveridad) => {
    updateEventoMutation.mutate({
      id: idEvento,
      data: { severidad: parseInt(nuevaSeveridad) },
    })
    if (onUpdate) onUpdate()
  }

  const getBadgeClass = (severidad) => {
    const classes = {
      1: 'badge-baja',
      2: 'badge-media',
      3: 'badge-alta',
    }
    return `badge ${classes[severidad] || ''}`
  }

  return (
    <Listbox value={severidadActual} onChange={handleChange} disabled={updateEventoMutation.isPending}>
      {({ open }) => (
        <>
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <Listbox.Button
              ref={buttonRef}
              onClick={(e) => e.stopPropagation()}
              className={`${getBadgeClass(severidadActual)} cursor-pointer hover:opacity-80 transition-opacity inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold`}
            >
              <span>{SEVERIDADES_LABELS[severidadActual]}</span>
              <ChevronDownIcon className="w-4 h-4" />
            </Listbox.Button>
          </div>
          {open && (
            <Portal>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options
                  static
                  className="fixed w-36 rounded-lg bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                  style={{
                    top: buttonRef.current ? buttonRef.current.getBoundingClientRect().bottom + 8 : 0,
                    left: buttonRef.current ? buttonRef.current.getBoundingClientRect().left : 0,
                  }}
                >
                  <div className="p-2" onClick={(e) => e.stopPropagation()}>
                    {Object.entries(severidadConfig).map(([key, config]) => (
                      <Listbox.Option
                        key={key}
                        value={parseInt(key)}
                        className={({ active }) =>
                          `flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                            active ? 'bg-gray-100' : ''
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                            <span className={`flex-1 text-sm text-gray-900 ${selected ? 'font-semibold' : 'font-normal'}`}>
                              {config.label}
                            </span>
                            {selected && <CheckIcon className="w-4 h-4 text-primary" />}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </div>
                </Listbox.Options>
              </Transition>
            </Portal>
          )}
        </>
      )}
    </Listbox>
  )
}

const EventosList = () => {
  const navigate = useNavigate()
  const {
    estadoEvento,
    idCentroTrabajo,
    idTipoAnomalia,
    severidad,
    idUsuarioCreador,
    page,
    viewMode,
    setEstadoEvento,
    setIdTipoAnomalia,
    setSeveridad,
    setIdUsuarioCreador,
    setPage,
    setViewMode,
  } = useFiltersStore()

  // Estado para el modal de confirmación de eliminación
  const [eventoAEliminar, setEventoAEliminar] = useState(null)

  // Hook para eliminar eventos
  const deleteEventoMutation = useDeleteEvento()

  // Hook para obtener centros de trabajo desde /api/ObtenerCentrosTrabajo
  const { data: centrosData, isLoading: isLoadingCentros } = useCentrosTrabajo()
  const centros = centrosData?.payload?.centrosTrabajo || []

  // Hook para obtener tipos de anomalía
  const { data: tiposAnomaliaData } = useTiposAnomalia()
  const tiposAnomalia = tiposAnomaliaData?.payload || []

  // Hook para obtener conteos de todos los estados (pasando idCentroTrabajo o undefined)
  const { conteos } = useEventosConteo(idCentroTrabajo || undefined)

  // Hook para obtener conteos por centro de trabajo (filtrado por estado activo)
  const { conteosPorCentro } = useEventosConteoPorCentro(centros, estadoEvento)

  // Función para manejar la eliminación
  const handleEliminar = () => {
    if (eventoAEliminar) {
      deleteEventoMutation.mutate(eventoAEliminar.idEvento)
      setEventoAEliminar(null)
    }
  }

  // Query de eventos - ahora el filtro se hace en el backend
  const {
    data: eventosData,
    isLoading,
    error,
  } = useEventos({
    page,
    pageSize: 20,
    estadoEvento,
    idCentroTrabajo, // Filtrar en el backend
  })

  // La API devuelve { code, payload, message } en lugar de { success, data, message }
  const eventos = eventosData?.payload?.Eventos || []
  const idEventos = eventos.map((e) => e.idEvento)

  // Query de thumbnails
  const { data: thumbnailsData } = useThumbnails(idEventos)
  const thumbnails = thumbnailsData?.payload?.Thumbnails || []

  // Crear mapa de thumbnails (añadiendo prefijo base64)
  const thumbnailsMap = {}
  thumbnails.forEach((t) => {
    thumbnailsMap[t.idEvento] = addBase64Prefix(t.thumbnail)
  })

  // Crear mapa de tipos de anomalía por id
  const tiposAnomaliaMap = {}
  tiposAnomalia.forEach((tipo) => {
    tiposAnomaliaMap[tipo.idTipoAnomalia] = tipo
  })

  // Obtener opciones únicas de filtros desde los eventos cargados
  const tiposAnomaliaEnPantalla = useMemo(() => {
    const uniqueTipos = new Map()
    eventos.forEach(evento => {
      if (evento.tipoAnomalia) {
        uniqueTipos.set(evento.tipoAnomalia.idTipoAnomalia, evento.tipoAnomalia)
      }
    })
    return Array.from(uniqueTipos.values())
  }, [eventos])

  const severidadesEnPantalla = useMemo(() => {
    const uniqueSev = new Set()
    eventos.forEach(evento => {
      if (evento.severidad) {
        uniqueSev.add(evento.severidad)
      }
    })
    return Array.from(uniqueSev).sort()
  }, [eventos])

  const autoresEnPantalla = useMemo(() => {
    const uniqueAutores = new Map()
    eventos.forEach(evento => {
      if (evento.usuarioCreador) {
        uniqueAutores.set(evento.usuarioCreador.id, evento.usuarioCreador)
      }
    })
    return Array.from(uniqueAutores.values())
  }, [eventos])

  // Filtrar eventos en el frontend según los filtros adicionales
  const eventosFiltrados = useMemo(() => {
    return eventos.filter(evento => {
      if (idTipoAnomalia && evento.tipoAnomalia?.idTipoAnomalia !== idTipoAnomalia) return false
      if (severidad && evento.severidad !== severidad) return false
      if (idUsuarioCreador && evento.usuarioCreador?.id !== idUsuarioCreador) return false
      return true
    })
  }, [eventos, idTipoAnomalia, severidad, idUsuarioCreador])

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6 sticky top-16 z-30">
        <div className="flex flex-wrap gap-2 mb-4 items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {Object.entries(ESTADOS).map(([key, value]) => {
              const Icon = ESTADOS_ICONS[value]
              const count = conteos[value]
              const isActive = estadoEvento === value
              // Si es el botón "Nuevo", no está activo y hay incidencias nuevas, mostrarlo en naranja
              const isNuevoConIncidencias = value === ESTADOS.NUEVO && !isActive && count > 0

              return (
                <button
                  key={value}
                  onClick={() => setEstadoEvento(value)}
                  className={`filter-btn ${isActive ? 'active' : ''} ${
                    isNuevoConIncidencias ? 'bg-orange-100 border-orange-400 text-orange-800 hover:bg-orange-200' : ''
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{ESTADOS_LABELS[value]}</span>
                  {count !== undefined && (
                    <span className="ml-1 font-semibold">({count})</span>
                  )}
                </button>
              )
            })}
          </div>

          {/* Botón de cambio de vista */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Vista de tarjetas"
            >
              <Squares2X2Icon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Vista de lista"
            >
              <ListBulletIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filtros adicionales y selector de centros */}
        <div className="pt-3 border-t border-gray-200 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Selector de centros de trabajo */}
            {!isLoadingCentros && centros.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Centro de trabajo</label>
                <CentrosSelector centros={centros} />
              </div>
            )}

            {/* Filtro por tipo de anomalía */}
            {tiposAnomaliaEnPantalla.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Tipo de anomalía</label>
                <Listbox value={idTipoAnomalia} onChange={setIdTipoAnomalia}>
                  <div className="relative">
                    <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left shadow-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm">
                      <span className="block truncate">
                        {idTipoAnomalia
                          ? tiposAnomaliaMap[idTipoAnomalia]?.nombre
                          : 'Todos los tipos'}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <Listbox.Option
                          value={null}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                              active ? 'bg-primary/10 text-primary' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                Todos los tipos
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary">
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                        {tiposAnomaliaEnPantalla.map((tipo) => (
                          <Listbox.Option
                            key={tipo.idTipoAnomalia}
                            value={tipo.idTipoAnomalia}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                active ? 'bg-primary/10 text-primary' : 'text-gray-900'
                              }`
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                  {tipo.nombre}
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary">
                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
              </div>
            )}

            {/* Filtro por gravedad */}
            {severidadesEnPantalla.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Gravedad</label>
                <Listbox value={severidad} onChange={setSeveridad}>
                  <div className="relative">
                    <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left shadow-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm">
                      <span className="block truncate">
                        {severidad ? SEVERIDADES_LABELS[severidad] : 'Todas las gravedades'}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <Listbox.Option
                          value={null}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                              active ? 'bg-primary/10 text-primary' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                Todas las gravedades
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary">
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                        {severidadesEnPantalla.map((sev) => (
                          <Listbox.Option
                            key={sev}
                            value={sev}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                active ? 'bg-primary/10 text-primary' : 'text-gray-900'
                              }`
                            }
                          >
                            {({ selected }) => (
                              <>
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SEVERIDADES_DOT_COLORS[sev] }} />
                                  <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                    {SEVERIDADES_LABELS[sev]}
                                  </span>
                                </div>
                                {selected && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary">
                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
              </div>
            )}

            {/* Filtro por autor */}
            {autoresEnPantalla.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Autor</label>
                <Listbox value={idUsuarioCreador} onChange={setIdUsuarioCreador}>
                  <div className="relative">
                    <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left shadow-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm">
                      <span className="block truncate">
                        {idUsuarioCreador
                          ? autoresEnPantalla.find(a => a.id === idUsuarioCreador)?.nombre
                          : 'Todos los autores'}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <Listbox.Option
                          value={null}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                              active ? 'bg-primary/10 text-primary' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                Todos los autores
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary">
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                        {autoresEnPantalla.map((autor) => (
                          <Listbox.Option
                            key={autor.id}
                            value={autor.id}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                active ? 'bg-primary/10 text-primary' : 'text-gray-900'
                              }`
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                  {autor.nombre}
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary">
                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
              </div>
            )}
          </div>

          {/* Resumen de eventos por centro */}
          {!isLoadingCentros && centros.length > 0 && (
            <div className="text-sm text-gray-600">
              {centros
                .map((centro) => ({
                  ...centro,
                  count: conteosPorCentro[centro.IdCentroTrabajo] || 0
                }))
                .filter((centro) => centro.count > 0)
                .sort((a, b) => b.count - a.count)
                .map((centro, index, array) => (
                  <span key={centro.IdCentroTrabajo}>
                    <strong>{centro.Nombre}</strong> ({centro.count})
                    {index < array.length - 1 && ', '}
                  </span>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Lista de eventos */}
      {isLoading ? (
        <Loading message="Cargando incidencias..." />
      ) : error ? (
        <EmptyState title="Error al cargar" message="No se pudieron cargar las incidencias" />
      ) : eventosFiltrados.length === 0 ? (
        <EmptyState
          title="No hay incidencias"
          message="No se encontraron incidencias con los filtros seleccionados"
        />
      ) : viewMode === 'grid' ? (
        // Vista de tarjetas (grid)
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {eventosFiltrados.map((evento, index) => (
            <article
              key={evento.idEvento}
              onClick={() => navigate(`/eventos/${evento.idEvento}`)}
              className={`rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 border overflow-hidden cursor-pointer animate-fade-in-up ${
                evento.severidad === 3 ? 'bg-red-100 border-red-300' : 'bg-white border-gray-200'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Banda de color según estado */}
              <div
                className={`h-1 ${
                  evento.estadoEvento === 1
                    ? 'bg-blue-500'
                    : evento.estadoEvento === 2
                    ? 'bg-purple-500'
                    : evento.estadoEvento === 3
                    ? 'bg-orange-500'
                    : evento.estadoEvento === 4
                    ? 'bg-green-500'
                    : 'bg-gray-500'
                }`}
              />

              <div className="p-4 relative">
                {/* Botón de eliminar en la esquina superior derecha */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setEventoAEliminar(evento)
                  }}
                  className="absolute top-2 right-2 p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700 transition-colors z-10"
                  title="Eliminar incidencia"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
                <div className="flex gap-4">
                  {/* Thumbnail - Más grande */}
                  <div className="w-32 h-32 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                    {thumbnailsMap[evento.idEvento] ? (
                      <img
                        src={thumbnailsMap[evento.idEvento]}
                        alt="Thumbnail"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                        Sin imagen
                      </div>
                    )}
                  </div>

                  {/* Contenido */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {/* Icono del tipo de anomalía */}
                      {evento.tipoAnomalia?.iconoUrl && (
                        <TipoAnomaliaIcon
                          iconoUrl={evento.tipoAnomalia.iconoUrl}
                          className="w-5 h-5 flex-shrink-0 text-gray-600"
                          alt={evento.tipoAnomalia.nombre}
                        />
                      )}
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {evento.tipoAnomalia?.nombre || 'Incidencia'}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 truncate">
                      {evento.ubicacion?.texto || 'Sin ubicación'}
                    </p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <SeveridadSelectorInline
                        idEvento={evento.idEvento}
                        severidadActual={evento.severidad}
                      />
                      <EstadoSelectorInline
                        idEvento={evento.idEvento}
                        estadoActual={evento.estadoEvento}
                      />
                      <span className="text-xs text-gray-500">
                        {formatRelativeTime(evento.fechaCreacionUtc, true)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Metadata - Iconos más grandes */}
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-600">
                  <div className="flex items-center gap-3">
                    {evento.cantidadImagenes > 0 && (
                      <span className="flex items-center gap-1 font-medium">
                        <span className="text-base">📷</span>
                        {evento.cantidadImagenes}
                      </span>
                    )}
                    {evento.cantidadComentarios > 0 && (
                      <span className="flex items-center gap-1 font-medium">
                        <span className="text-base">💬</span>
                        {evento.cantidadComentarios}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">{evento.usuarioCreador?.nombre}</div>
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        // Vista de lista
        <div className="space-y-3">
          {eventosFiltrados.map((evento, index) => (
            <article
              key={evento.idEvento}
              onClick={() => navigate(`/eventos/${evento.idEvento}`)}
              className={`rounded-lg shadow-sm hover:shadow-md transition-all duration-200 border overflow-hidden cursor-pointer animate-fade-in-up ${
                evento.severidad === 3 ? 'bg-red-50 border-red-300' : 'bg-white border-gray-200'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-stretch gap-4 p-3">
                {/* Thumbnail más grande, ajustado al alto del contenedor */}
                <div className="w-24 rounded-md overflow-hidden flex-shrink-0 bg-gray-100 self-stretch">
                  {thumbnailsMap[evento.idEvento] ? (
                    <img
                      src={thumbnailsMap[evento.idEvento]}
                      alt="Thumbnail"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                      Sin imagen
                    </div>
                  )}
                </div>

                {/* Contenido principal: Icono, nombre y descripción */}
                <div className="flex flex-col gap-2 min-w-0 flex-1 justify-between">
                  <div className="flex items-start gap-2">
                    {evento.tipoAnomalia?.iconoUrl && (
                      <TipoAnomaliaIcon
                        iconoUrl={evento.tipoAnomalia.iconoUrl}
                        className="w-5 h-5 flex-shrink-0 text-gray-600 mt-0.5"
                        alt={evento.tipoAnomalia.nombre}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-semibold text-gray-900">
                        {evento.tipoAnomalia?.nombre || 'Incidencia'}
                        {evento.descripcion && (
                          <span className="font-normal text-gray-500 ml-2">
                            - {evento.descripcion}
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-gray-600 truncate">
                        {evento.ubicacion?.texto || 'Sin ubicación'}
                      </p>
                    </div>
                  </div>

                  {/* Badges horizontales (Gravedad y Estado) */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <SeveridadSelectorInline
                      idEvento={evento.idEvento}
                      severidadActual={evento.severidad}
                    />
                    <EstadoSelectorInline
                      idEvento={evento.idEvento}
                      estadoActual={evento.estadoEvento}
                    />
                  </div>
                </div>

                {/* Metadata */}
                <div className="flex flex-col gap-2 flex-shrink-0 self-start text-right">
                  <div className="text-xs text-gray-500 whitespace-nowrap">
                    {formatRelativeTime(evento.fechaCreacionUtc, true)}
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    {evento.cantidadImagenes > 0 && (
                      <span className="flex items-center gap-1 text-xs font-medium text-gray-600">
                        <span>📷</span>
                        {evento.cantidadImagenes}
                      </span>
                    )}
                    {evento.cantidadComentarios > 0 && (
                      <span className="flex items-center gap-1 text-xs font-medium text-gray-600">
                        <span>💬</span>
                        {evento.cantidadComentarios}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 whitespace-nowrap">{evento.usuarioCreador?.nombre}</div>
                </div>

                {/* Botón de eliminar */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setEventoAEliminar(evento)
                  }}
                  className="p-1.5 rounded-md bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700 transition-colors flex-shrink-0 self-start"
                  title="Eliminar incidencia"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Paginación simple */}
      {eventosData?.payload?.Pagination && (
        <div className="flex justify-center items-center gap-4 mt-8">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="btn-secondary disabled:opacity-50"
          >
            Anterior
          </button>
          <span className="text-sm text-gray-700">
            Página {page} de {eventosData.payload.Pagination.TotalPages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= eventosData.payload.Pagination.TotalPages}
            className="btn-secondary disabled:opacity-50"
          >
            Siguiente
          </button>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      <Transition show={eventoAEliminar !== null} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setEventoAEliminar(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                      <TrashIcon className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <Dialog.Title as="h3" className="text-lg font-bold text-gray-900">
                        Eliminar incidencia
                      </Dialog.Title>
                    </div>
                  </div>

                  <Dialog.Description className="text-sm text-gray-600 mb-6">
                    ¿Estás seguro de que deseas eliminar esta incidencia?
                    {eventoAEliminar && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="font-semibold text-gray-900">
                          {eventoAEliminar.tipoAnomalia?.nombre || 'Incidencia'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {eventoAEliminar.ubicacion?.texto || 'Sin ubicación'}
                        </p>
                      </div>
                    )}
                    <p className="mt-3 font-semibold text-red-600">
                      Esta acción no se puede deshacer.
                    </p>
                  </Dialog.Description>

                  <div className="flex gap-3 justify-end">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setEventoAEliminar(null)}
                      disabled={deleteEventoMutation.isPending}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
                      onClick={handleEliminar}
                      disabled={deleteEventoMutation.isPending}
                    >
                      {deleteEventoMutation.isPending ? 'Eliminando...' : 'Eliminar'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}

export default EventosList
